"use client";
import { IndexCard } from "@/types";
import { Filters } from "@/types/General";
import { Dispatch, SetStateAction, useCallback, useState } from "react";

export const useFilters = (
  initialState: Filters,
): {
  selectedFilters: Filters;
  setSelectedFilters: Dispatch<SetStateAction<Filters>>;
  onSelectFilters: (name: string, paths: string[]) => Filters;
  onDeselectFilters: (name: string, paths: string[]) => Filters;
  setFilter: (name: string, paths: string[]) => Filters;
  onClearAll: () => Filters;
} => {
  const [selectedFilters, setSelectedFilters] = useState(initialState);

  const setFilter = (name: string, paths: string[]): Filters => {
    const newFilters = [...selectedFilters];
    const newFilter = newFilters.find(({ name: newName }) => newName === name);

    if (!newFilter) {
      newFilters.push({
        name,
        paths,
      });
    } else {
      newFilter.paths = Array.from(new Set(paths));
      if (newFilter.paths.length === 0) {
        const newFilterIndex = newFilters.findIndex(
          ({ name: newName }) => newName === name,
        );
        newFilters.splice(newFilterIndex, 1);
      }
    }
    setSelectedFilters(newFilters);
    return newFilters;
  };

  const onSelectFilters = (name: string, paths: string[]): Filters => {
    const newFilters = [...selectedFilters];
    const newFilter = newFilters.find(({ name: newName }) => newName === name);

    if (!newFilter) {
      newFilters.push({
        name,
        paths,
      });
    } else {
      newFilter.paths = Array.from(new Set([...newFilter.paths, ...paths]));
    }
    setSelectedFilters(newFilters);
    return newFilters;
  };

  const onDeselectFilters = (name: string, paths: string[]): Filters => {
    const newFilters = [...selectedFilters];
    const newFilter = newFilters.find(({ name: newName }) => newName === name);

    if (!newFilter) {
      return selectedFilters;
    } else {
      newFilter.paths = newFilter.paths.filter(
        (filter) => !paths.includes(filter),
      );
    }

    if (newFilter.paths.length === 0) {
      const newFilterIndex = newFilters.findIndex(
        ({ name: newName }) => newName === name,
      );
      newFilters.splice(newFilterIndex, 1);
    }
    setSelectedFilters(newFilters);
    return newFilters;
  };
  const onClearAll = (): Filters => {
    setSelectedFilters([]);
    return [];
  };

  return {
    selectedFilters,
    onSelectFilters,
    onDeselectFilters,
    onClearAll,
    setSelectedFilters,
    setFilter,
  };
};

export const useQueryParams = (
  filters: {
    id: string;
    data: IndexCard;
  }[],
) => {
  return useCallback(
    (selectedFilters: Filters, searchExpression?: string) => {
      const newUrl = new URL(window.location.href);
      const newParams = new URLSearchParams(newUrl.searchParams);

      const allParameters = Array.from(
        new Set(
          filters?.flatMap((filter) =>
            filter.data.categorization?.map(
              ({ parameterName }) => parameterName,
            ),
          ),
        ),
      );

      for (const parameter of allParameters) {
        if (parameter) {
          const selectedFilter = selectedFilters?.find(
            (selectedFilter) => selectedFilter.name === parameter,
          );

          if (selectedFilter) {
            newParams.set(selectedFilter.name, selectedFilter.paths.toString());
          }
          if (newParams.get(parameter) && !selectedFilter) {
            newParams.delete(parameter);
          }
        }
      }

      if (searchExpression) {
        if (
          !newParams.get("searchTerm") ||
          searchExpression.toLowerCase() !==
            newParams.get("searchTerm")?.toLowerCase()
        ) {
          newParams.set("searchTerm", searchExpression);
        }
      } else {
        if (newParams.get("searchTerm")) {
          newParams.delete("searchTerm");
        }
      }

      newUrl.search = newParams.toString();

      if (newUrl.toString() !== window.location.href) {
        window.history.replaceState({}, "", newUrl.toString());
      }
    },
    [filters],
  );
};
